import { useState, useEffect } from "react";
import PendingDataWarning from "@shared/components/PendingDataWarning";
import AppUpdateModal from "../components/AppUpdateModal";
import Container from "@material-ui/core/Container";
// import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { Redirect, Link, useHistory } from "react-router-dom";
// import FullLoadingScreen from "@shared/components/FullLoadingScreen";
import * as mobileDetect from "react-device-detect";
import RenderLoginForm from "@shared/components/RenderLoginForm";
// import OldDomainWarning from "@shared/components/OldDomainWarning";
import { useDispatch, useSelector, useStore } from "react-redux";
import { resetAuth } from "../store/slices/auth";
// import { selectAuth } from "../store/slices/auth";
import { setDevice } from "../store/slices/miscData";
import { selectQueues } from "../store/slices/syncronization";
import { setDisplayNav } from "../store/slices/ui";
import { useAuth } from "../Hooks/useAuth";
import usePouch from "../Hooks/usePouch";
import usePouchQueues from "../Hooks/usePouchQueues";
import api from "@shared/server/api";
import { getLocalToken } from "@shared/functions";
import { logger } from "../functions/logger";
import { logCuadreSliceReset } from "../functions/logCuadreSliceReset";
import { withTimeout } from "../functions/withTimeout";
import { clearDataListCache } from "../Hooks/useDataList";
import { localPouchPrefix, pouches_db } from "../config"; //localStorageCommon
import { selectCommon, setCommon } from "../store/slices/common";
// import PouchDB from "pouchdb-browser";

// Bounds on the login-path awaits so a hung request/DB op can never leave the
// login button stuck spinning (Formik releases it only when submithandler settles).
const NETWORK_TIMEOUT_MS = 20000; // per axios request on the login path
const DESTROY_TIMEOUT_MS = 10000; // per PouchDB deleteDatabase()/compact() call

// Bound a local-DB wipe/compact but NEVER let it fail the login. A live:true
// replication can hold a DB open so destroy() exceeds the timeout — withTimeout
// then rejects. If we let that throw, the whole login aborts and the user is
// locked out on every attempt (the destroy keeps hanging). A stale local DB is
// the lesser evil: it gets overwritten/re-synced, and gastos already has a
// route-filter guard. So we swallow the timeout, log it, and continue the login.
const safeDestroy = async (label, dbPromise) => {
  try {
    await withTimeout(dbPromise, DESTROY_TIMEOUT_MS, label);
  } catch (e) {
    logger.warning("login.destroy_timeout", `Local wipe timed out; continuing login: ${label}`, {
      entity: "login",
      context: { label, error: String(e?.message || e), isTimeout: !!e?.isTimeout },
    });
  }
};

const Login = () => {
  const history = useHistory();
  const payments_db = usePouch(pouches_db.PAYMENTS);
  const gastos_db = usePouch(pouches_db.GASTOS);
  const loans_db = usePouch(pouches_db.LOANS);
  const debtors_db = usePouch(pouches_db.DEBTORS);
  // const debtorsReorder_db = usePouch(pouches_db.DEBTORS_REORDER);
  const newLoans_db = usePouch(pouches_db.NEW_LOANS);
  const newDebtors_db = usePouch(pouches_db.NEW_DEBTORS);
  const conflicts_db = usePouch(pouches_db.SYNC_CONFLICTS);
  const offline_db = usePouch(pouches_db.OFFLINE_DATA);
  const queues_db = usePouch(pouches_db.SYNC_QUEUES);
  const history_db = usePouchQueues(pouches_db.SYNC_HISTORY);

  const [errors, setErrors] = useState(null);
  // const [errorDisplay, setErrorDisplay] = useState(null);
  const auth = useAuth();
  const queues = useSelector(selectQueues);
  const common = useSelector(selectCommon);
  const dispatch = useDispatch();
  const store = useStore();
  const [appUpdate, setAppUpdate] = useState(false);
  const {
    browserName,
    fullBrowserVersion,
    mobileVendor,
    mobileModel,
    osVersion,
    getUA,
  } = mobileDetect;
  const filteredQueues = queues.filter((q) => q.requiredData === true);

  const getDeviceSpecs = () => {
    let displayMode = "Browser";
    if (window.matchMedia("(display-mode: standalone)").matches) {
      displayMode = "PWA Standalone";
    }

    return {
      displayMode,
      appVersion: import.meta.env.VITE_VERSION,
      foceAppUpdate: import.meta.env.VITE_foceAppUpdateNumber,
      browserName,
      fullBrowserVersion,
      mobileVendor,
      mobileModel,
      osVersion,
      getUA,
    };
  };

  useEffect(() => {
    dispatch(resetAuth());
    dispatch(setDisplayNav({ DisplayNav: false }));
    dispatch(setDevice(getDeviceSpecs()));
  }, []);

  const deleteExpiredDocs = async () => {
    const docs = await offline_db.getAllDocs();

    const now = Date.now();
    const expired = docs
      .filter((doc) => doc.meta.expiresAt && doc.meta.expiresAt < now)
      .map((doc) => ({
        _id: doc._id,
        _rev: doc._rev,
        _deleted: true,
      }));

    if (expired.length > 0) {
      await offline_db.bulkDocs(expired);
    }

    await safeDestroy("compact:offline", offline_db.compact());
  };

  const emptySyncIfQueueEmpty = async () => {
    const queues = await queues_db.getAllDocs();

    if (queues.length === 0) {
      console.log("Syncronization DB is now empty.");
      // queues DB has an always-on live sync (App.jsx queuesSync) that can hold it
      // open and hang destroy() — bound both so the login can't stall here.
      await safeDestroy("destroy:history", history_db.deleteDatabase());
      await safeDestroy("destroy:queues", queues_db.deleteDatabase());
    } else {
      console.log("Syncronization DB is kept because it still has data.");
    }
  };

  // useEffect(() => {
  //   const interval = setTimeout(() => {
  //     dispatch(resetAuth());
  //   }, 5000);

  //   return () => clearTimeout(interval);
  // }, [auth.error]);

  if (common?.cuadre_id && auth.data.jwt !== null)
    return <Redirect to="/swr/fetcher" />;

  const submithandler = async (data) => {
    // Breadcrumb trail: mark() is called before each phase so the LAST log before a
    // hang names the step that stuck, and currentStep is reported in the catch. Logs
    // replicate independently of this flow, so breadcrumbs survive even if it hangs.
    const t0 = performance.now();
    let currentStep = "start";
    const mark = (step, extra = {}) => {
      currentStep = step;
      logger.info("auth.login.step", step, {
        context: { step, elapsed_ms: Math.round(performance.now() - t0), ...extra },
      });
    };

    try {
      const token = getLocalToken();

      // const device_db = new PouchDB(`${import.meta.env.VITE_COUCH_URL}/queues-${token}`);

      // try {
      //   const compact = await device_db.compact();
      //   console.log({ compact });
      // } catch (err) {
      //   console.log(err);
      // }

      const ipAddress = "1.0.0.27";
      // const rData = await auth.login({ ...data, token });
      mark("login_request");
      const { data: rData } = await api.post(
        "/cobro/login/v2",
        {
          data: { ...data, token, ipAddress },
          foceAppUpdateNumber: import.meta.env.VITE_foceAppUpdateNumber,
        },
        { timeout: NETWORK_TIMEOUT_MS }
      );

      // if (!rData) return;

      if (rData.action === "routeDisabled") {
        history.push(`/customer/disabled/${rData.customer.customer_id}`);
        // setErrorDisplay("Su cuenta esta desabilitada por falta de pago!");
        return;
      }

      const { user_id, creditor_id, owner_id } = rData.user;

      mark("cuadre_init");
      const { data: dbCommon } = await api.post(
        "/cobro/cuadre/init",
        {
          creditor_id,
          owner_id,
          user_id,
          token,
          env: import.meta.env.VITE_ENV,
          appVersion: import.meta.env.VITE_VERSION,
        },
        { timeout: NETWORK_TIMEOUT_MS }
      );

      const now = Date.now();
      const meta = { expiresAt: now + 60 * 60 * 24 * 30 * 1000 };

      // const localCommon = await offline_db.getDocById("common");

      const lastUsedCuadreId = localStorage.getItem("localCommonId");

      if (lastUsedCuadreId !== dbCommon.cuadre_id) {
        // localStorage.setItem(localStorageCommon, JSON.stringify(dbCommon));

        localStorage.setItem(
          "couch_target",
          rData?.user?.couch_target ?? "new"
        );

        localStorage.setItem("eps_debtors_state", "0");

        localStorage.setItem("eps_loans_state", "0");

        localStorage.setItem("eps_payments_state", "0");

        const oldPrefix = localStorage.getItem(localPouchPrefix);
        const prefix = `w${dbCommon.owner_id}_r${dbCommon.creditor_id}_${dbCommon.collect_date}_c${dbCommon.cuadre_id}`;
        localStorage.setItem(localPouchPrefix, prefix);

        await offline_db.putSert({ _id: "common", ...dbCommon, meta });

        // Diagnostic: capture what's in local gastos right before the wipe (which
        // route's docs were present, and confirms the wipe is actually running).
        const preWipeGastos = await gastos_db.getAllDocs({ include_deleted: true });
        logger.warning("gastos.pre_wipe", "Wiping local gastos on cuadre switch", {
          entity: "gastos",
          context: {
            last_cuadre_id: lastUsedCuadreId,
            new_cuadre_id: dbCommon.cuadre_id,
            old_prefix: oldPrefix,
            new_prefix: prefix,
            local_gastos_count: preWipeGastos.length,
            local_gastos_prefixes: [...new Set(preWipeGastos.map((d) => d.prefix ?? null))],
          },
        });

        // deleteDatabase() -> PouchDB destroy(), which hangs indefinitely if a
        // live:true replication holds the DB open. Bound each so a blocked destroy
        // surfaces as a (loggable) timeout instead of a stuck login button.
        mark("wipe_dbs");
        await safeDestroy("destroy:loans", loans_db.deleteDatabase());
        await safeDestroy("destroy:debtors", debtors_db.deleteDatabase());

        await safeDestroy("destroy:payments", payments_db.deleteDatabase());
        await safeDestroy("destroy:gastos", gastos_db.deleteDatabase());
        await safeDestroy("destroy:newDebtors", newDebtors_db.deleteDatabase());
        await safeDestroy("destroy:newLoans", newLoans_db.deleteDatabase());

        await safeDestroy("destroy:conflicts", conflicts_db.deleteDatabase());
        await emptySyncIfQueueEmpty();

        // Drop the in-memory list cache so a previous route's docs can't be served
        // from it after this wipe (the module-scoped cache survives the SPA session).
        clearDataListCache();

        // Populate pouch DB with Debtors.
        mark("reload_debtors");
        const { data: debtors } = await api.get(
          `/debtors/${creditor_id}/${user_id}`,
          { timeout: NETWORK_TIMEOUT_MS }
        );
        mark("reload_debtors_fetched", { count: debtors.length });
        debtors.forEach(function (row) {
          row._id = row.debtor_id;
        });
        await debtors_db.bulkDocs(debtors);

        // Mark this cuadre as last-used ONLY after the wipe + debtor reload
        // succeeded. If anything above threw, the guard stays on the OLD cuadre so a
        // retry re-runs the full wipe instead of skipping it (persistent stale bug).
        localStorage.setItem("localCommonId", dbCommon.cuadre_id);

        // Race check: local gastos must be empty right after a wipe. A non-zero
        // count means an old-prefix live sync re-populated it during the destroy
        // window — the definitive proof of the resurrection race.
        const postWipeGastos = await gastos_db.getAllDocs({ include_deleted: true });
        if (postWipeGastos.length > 0) {
          logger.error("gastos.post_wipe_nonzero", "Local gastos NOT empty after wipe (race/resurrection)", {
            entity: "gastos",
            context: {
              new_cuadre_id: dbCommon.cuadre_id,
              new_prefix: prefix,
              count: postWipeGastos.length,
              prefixes: [...new Set(postWipeGastos.map((d) => d.prefix ?? null))],
            },
          });
        }

        // Cuadre changed: flush this session's audit logs to the server, then reset
        // the local audit-log DB to bound its growth per-cuadre. Runs ONLY here (not
        // on every login) so a re-login to the same cuadre keeps its pending logs.
        // destroy() is local-only, so the server's audit_logs are preserved.
        await logger.purgeLocal();

        // Populate pouch DB with loans.
        //FIXME: When loans are fetched it will make cuadre init for common to fetch  a new creditor_id.
        // const { data: loansResult } = await api.get(`/cobro/loansv3/${user_id}/${creditor_id}`);
        // loansResult.loans.forEach(function (row2) {
        //   row2._id = row2.money_id;
        // });
        // await loans_db.bulkDocs(loansResult.loans);

        // Populate pouch DB with Debtors Reoder.
        // loansResult.debtorsReorded.forEach(function (row3) {
        //   row3._id = row3.dUniqueId;
        // });
        // await debtorsReorder_db.bulkDocs(loansResult.debtorsReorded);
      } else {
        // Same cuadre_id as last time -> no wipe. If the local gastos still hold
        // docs from another route (e.g. an earlier interrupted switch), that's how
        // previous-route data persists across refreshes; capture it.
        const keptGastos = await gastos_db.getAllDocs({ include_deleted: true });
        const currentPrefix = localStorage.getItem(localPouchPrefix);
        const foreign = keptGastos.filter((d) => d.prefix && currentPrefix && d.prefix !== currentPrefix);
        logger.info("gastos.wipe_skipped", "Login re-used same cuadre; local DBs NOT wiped", {
          entity: "gastos",
          context: {
            cuadre_id: dbCommon.cuadre_id,
            current_prefix: currentPrefix,
            local_gastos_count: keptGastos.length,
            foreign_count: foreign.length,
            foreign_prefixes: [...new Set(foreign.map((d) => d.prefix ?? null))],
          },
        });
      }

      // Store pouch DB USer.
      mark("load_user");
      const pouch_user_id = `w${user_id}`;
      const pouch_user = await offline_db.getDocById(pouch_user_id);

      if (pouch_user) {
        pouch_user.meta.expiresAt = meta.expiresAt;
        await offline_db.putDoc(pouch_user);
      } else {
        const { data: db_user } = await api.get(`/user/${user_id}`, {
          timeout: NETWORK_TIMEOUT_MS,
        });
        await offline_db.putDoc({ _id: pouch_user_id, ...db_user, meta });
      }

      // Store pouch DB Route.
      mark("load_route");
      const pouch_route_id = `r${creditor_id}`;
      const pouch_route = await offline_db.getDocById(pouch_route_id);
      if (pouch_route) {
        pouch_route.meta.expiresAt = meta.expiresAt;
        await offline_db.putDoc(pouch_route);
      } else {
        const { data: db_route } = await api.get(
          `/route/info/${creditor_id}/${user_id}`,
          { timeout: NETWORK_TIMEOUT_MS }
        );
        await offline_db.putDoc({ _id: pouch_route_id, ...db_route, meta });
      }

      // Store pouch DB owner Config.
      mark("load_owner_config");
      const pouch_owner_config_id = `oc${owner_id}`;
      const pouch_owner_config = await offline_db.getDocById(
        pouch_owner_config_id
      );
      if (pouch_owner_config) {
        pouch_owner_config.meta.expiresAt = meta.expiresAt;
        await offline_db.putDoc(pouch_owner_config);
      } else {
        const { data: db_owner_configs } = await api.get(
          `/owner/configv2/${owner_id}`,
          { timeout: NETWORK_TIMEOUT_MS }
        );
        await offline_db.putDoc({
          _id: pouch_owner_config_id,
          ...db_owner_configs,
          meta,
        });
      }

      // Store pouch DB USer Config.
      mark("load_user_config");
      const pouch_user_config_id = `uc${user_id}`;
      const pouch_user_config = await offline_db.getDocById(
        pouch_user_config_id
      );
      if (pouch_user_config) {
        pouch_user_config.meta.expiresAt = meta.expiresAt;
        await offline_db.putDoc(pouch_user_config);
      } else {
        const { data: db_user_configs } = await api.get(
          `/user/configv2/${user_id}`,
          { timeout: NETWORK_TIMEOUT_MS }
        );
        await offline_db.putDoc({
          _id: pouch_user_config_id,
          ...db_user_configs,
          meta,
        });
      }

      // Store pouch DB Permissions.
      mark("load_permissions");
      const pouch_permissions_id = `p${user_id}${creditor_id}`;
      const pouch_permissions = await offline_db.getDocById(
        pouch_permissions_id
      );
      if (pouch_permissions) {
        pouch_permissions.meta.expiresAt = meta.expiresAt;
        await offline_db.putDoc(pouch_permissions);
      } else {
        const { data: db_permissions } = await api.get(
          `/user/route/permissions/${user_id}/${creditor_id}`,
          { timeout: NETWORK_TIMEOUT_MS }
        );
        await offline_db.putDoc({
          _id: pouch_permissions_id,
          ...db_permissions,
          meta,
        });
      }

      mark("finalize");
      await deleteExpiredDocs();

      auth.setRememberData({ ...data, ...rData.user });

      auth.setAuthData(rData);

      // Same-cuadre re-login is the case where the slices MUST survive, so record
      // the verdict before dispatching. Read the store rather than a selector: the
      // wipe block above may have run, so a render-time value would be stale.
      logCuadreSliceReset(store.getState(), dbCommon?.cuadre_id ?? null, "login");

      dispatch(setCommon(dbCommon));

      // Logged on EVERY login for the audit trail. (purgeLocal now runs only inside
      // the cuadre-change block above.)
      logger.info("auth.login", "User logged in", { context: { cuadre_id: dbCommon?.cuadre_id } });

      // if (dbCommon && rData && auth.data && auth.isLoading === false) {
      //   dispatch(authThunk({ data }));
      // }
    } catch (error) {
      console.log(error);

      // A timeout (our withTimeout, or axios ECONNABORTED) means a step hung rather
      // than a normal API rejection — log it at error level so it surfaces even under
      // a warning prod threshold, and record which step it died on.
      const isTimeout = error?.isTimeout || error?.code === "ECONNABORTED";
      logger[isTimeout ? "error" : "warning"]("auth.login_failed", "Login failed", {
        context: {
          step: currentStep,
          elapsed_ms: Math.round(performance.now() - t0),
          timeout: isTimeout,
          username: data?.username,
          error: error?.response?.data?.message || error?.message,
        },
      });

      if (error.response) {
        setErrors(error.response.data.message);
      } else {
        setErrors(error.message);
      }
    }
  };

  return (
    // <OldDomainWarning url="https://beta-cobro.wprestamos.com" label="beta-cobro.wprestamos.com" />
    <Container fixed maxWidth="sm">
      {appUpdate && <AppUpdateModal />}

      {/* {authDiscontinued.commonError && (
        <div className="alert alert-danger mt-3">
          <Typography variant="subtitle2" component="p">
            {authDiscontinued.commonError}
          </Typography>
        </div>
      )} */}

      {filteredQueues.length > 50 ? (
        <PendingDataWarning pendingData={filteredQueues} />
      ) : (
        <>
          {/* {errorDisplay && (
            <div className="alert alert-danger mt-3">
              <Typography variant="subtitle2" component="p">
                {errorDisplay}
              </Typography>
            </div>
          )} */}

          {auth.errors && (
            <div className="alert alert-danger mt-3">
              <Typography variant="subtitle2" component="p">
                {auth.errors}
              </Typography>
            </div>
          )}

          {errors && (
            <div className="alert alert-danger mt-3">
              <Typography variant="subtitle2" component="p">
                {errors}
              </Typography>
            </div>
          )}

          <RenderLoginForm submithandler={submithandler} />
        </>
      )}

      {/* <div style={{ marginTop: 160, marginBottom: 50 }} className="text-center">
        <Link to="/clear/app/data" className="h6">
          <span className="text-danger">Reiniciar Aplicaci&oacute;n (Borrar Todo)</span>
        </Link>
      </div> */}
    </Container>
  );
};

export default Login;
